import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { Button, OnlineLink, GenericSwitch } from "components";
import { hasPermissions, permissionList } from "helpers";
import useCheckIfUsedPaymentMutation from "queries/paymentMethods/useCheckIfUsedPaymentMutation";
import { uiActions } from "store";
import { PaymentTypes, StatusPaymentMethodTypes } from "types";
import { PaymentMethodValues } from "types/paymentMethod";

import { ReactComponent as RemoveIcon } from "static/images/cancel.svg";
import { ReactComponent as PencilEditIcon } from "static/images/pencil_edit.svg";
import styles from "../../paymentMethods.module.scss";

const actionColumn = ({
	dispatch,
	removePaymentMethod,
	updatePaymentMethod,
	userId,
	loading,
}) => ({
	Header: "Actions",
	accessor: "",
	disableSortBy: true,
	classNameHeader: styles.actionsCellHeader,
	classNameCell: styles.actionsCellColumn,
	Cell: ({ row }) => {
		const location = useLocation();

		const {
			id: paymentMethodId,
			paymentMethodStatus,
			paymentType,
		}: PaymentMethodValues & { id } = row.original;

		const { mutateAsync: checkIfUsedPaymentMutation } =
			useCheckIfUsedPaymentMutation();

		const handleStatusChange = useCallback(
			({ target }) => {
				updatePaymentMethod({
					paymentMethodId: paymentMethodId,
					userId,
					active: target.checked,
				});
			},
			[paymentMethodId],
		);

		const handleRemovePaymentMethod = async event => {
			event.stopPropagation();
			checkIfUsedPaymentMutation({
				paymentMethodId,
			}).then(isUsed => {
				if (isUsed) {
					dispatch(
						uiActions.showModal("DELETE_PAYMENT_METHOD", {
							paymentMethod: row.original,
							handleSubmit: () =>
								removePaymentMethod({
									userId,
									paymentMethodId,
								}),
						}),
					);
				} else {
					removePaymentMethod({
						userId,
						paymentMethodId,
					});
				}
			});
		};

		const isCreditCard = paymentType?.name === PaymentTypes.creditCard.name;

		return (
			<div className="d-flex flex-row justify-content-between">
				<div className="d-flex flex-row">
					<GenericSwitch
						style={{ margin: 10 }}
						name={`active_${paymentMethodId}`}
						id={`active_${paymentMethodId}`}
						enabled={paymentMethodStatus}
						isDisabled={
							loading.isLoading &&
							loading.idPaymentMethodUpdated === paymentMethodId
						}
						onChange={handleStatusChange}
					/>
					<span className="ml-3 mt-2">
						{
							StatusPaymentMethodTypes.getByValue(
								paymentMethodStatus
									? StatusPaymentMethodTypes.Active.value
									: StatusPaymentMethodTypes.Inactive.value,
							)?.displayName
						}
					</span>
				</div>
				<div className="ml-3 mt-1">
					<i className="column-separator mr-3" />

					<Button
						btnIcon
						icon={<PencilEditIcon />}
						onClick={e => e.stopPropagation()}
						as={OnlineLink}
						to={`${location.pathname}/${row?.original?.id}/edit`}
						variant="secondary"
						size="sm"
						title="Edit"
						className={
							!hasPermissions(
								permissionList.ViewSensitiveAccountDataFull,
							) || isCreditCard
								? "disabled"
								: ""
						}
						disabled={
							!hasPermissions(
								permissionList.ViewSensitiveAccountDataFull,
							) || isCreditCard
						}
					/>

					<Button
						onClick={handleRemovePaymentMethod}
						size="sm"
						icon={<RemoveIcon />}
						btnIcon
						variant="secondary"
						title="Remove"
						disabled={
							!hasPermissions(
								permissionList.ViewSensitiveAccountDataFull,
							)
						}
					/>
				</div>
			</div>
		);
	},
});

export default actionColumn;
